export function isUomId(val) {
  if (typeof val !== 'string') return false;
  const trimmed = val.trim();
  return /^UO[MN]-/i.test(trimmed) || /^[A-Z]{2,6}-\d+(-\d+)?$/i.test(trimmed);
}

export function resolveHumanUom(line = {}) {
  const candidate =
    line.uom_details?.name ??
    line.item?.uom ??
    line.item?.uom_details?.name ??
    (typeof line.uom === 'string' && !isUomId(line.uom) ? line.uom : undefined);

  if (candidate && typeof candidate === 'string' && !isUomId(candidate)) {
    return candidate;
  }
  return null;
}

function getUnitName(unitDetails) {
  const name = unitDetails?.name ?? unitDetails?.short_code ?? unitDetails?.shortCode ?? null;
  if (!name || isUomId(name)) return null;
  return name;
}

function getUnitType(unitDetails) {
  const type = String(unitDetails?.uom_type ?? unitDetails?.uomType ?? '').toUpperCase();
  const resolveType = String(
    unitDetails?.resolve_type ?? unitDetails?.resolveType ?? '',
  ).toUpperCase();

  if (type === 'PORTION' && (resolveType === 'WEIGHT' || resolveType === 'VOLUME')) {
    return resolveType;
  }

  return type;
}

function isResolvedPortionUnit(unitDetails) {
  const type = String(unitDetails?.uom_type ?? unitDetails?.uomType ?? '').toUpperCase();
  const resolveType = String(
    unitDetails?.resolve_type ?? unitDetails?.resolveType ?? '',
  ).toUpperCase();

  return type === 'PORTION' && (resolveType === 'WEIGHT' || resolveType === 'VOLUME');
}

function firstNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function formatDecimalQuantity(value) {
  if (!Number.isFinite(value)) return null;
  return Number(value.toFixed(3)).toString();
}

function getMetricUnitFactor(unitDetails) {
  const type = getUnitType(unitDetails);
  const code = String(unitDetails?.short_code ?? unitDetails?.shortCode ?? unitDetails?.name ?? '')
    .toLowerCase()
    .trim();

  if (type === 'WEIGHT') {
    if (code === 'g' || code === 'gram' || code === 'grams') return 0.001;
    if (code === 'mg' || code === 'milligram' || code === 'milligrams') return 0.000001;
    if (code === 'kg' || code === 'kilogram' || code === 'kilograms') return 1;
  }

  if (type === 'VOLUME') {
    if (code === 'ml' || code === 'milliliter' || code === 'milliliters') return 0.001;
    if (code === 'l' || code === 'liter' || code === 'liters') return 1;
    if (code === 'ltr' || code === 'litre' || code === 'litres') return 1;
  }

  return firstNumber(unitDetails?.conversion_factor, unitDetails?.conversionFactor);
}

function getAlternativeUnitQuantityLabel(
  alternativeDetails,
  alternativeUnitDetails,
  baseUnitDetails,
) {
  const unitName = getUnitName(alternativeUnitDetails);
  const conversionRate = firstNumber(
    alternativeDetails?.conversion_rate,
    alternativeDetails?.conversionRate,
  );
  const alternativeFactor = getMetricUnitFactor(alternativeUnitDetails);
  const baseFactor = getMetricUnitFactor(baseUnitDetails);

  if (!unitName) return null;
  if (!conversionRate || !alternativeFactor || !baseFactor) return unitName;

  const quantity = formatDecimalQuantity((conversionRate * baseFactor) / alternativeFactor);
  return quantity ? `${quantity} ${unitName}` : unitName;
}

function getPackAlternativeUnitLabel(alternativeDetails, alternativeUnitDetails, baseUnitDetails) {
  const alternativeUnitName = getUnitName(alternativeUnitDetails);
  const baseUnitName = getUnitName(baseUnitDetails);
  const conversionRate = firstNumber(
    alternativeDetails?.conversion_rate,
    alternativeDetails?.conversionRate,
  );

  if (!alternativeUnitName) return null;
  if (!conversionRate || !baseUnitName) return alternativeUnitName;

  return `${formatDecimalQuantity(conversionRate)} ${baseUnitName} (${alternativeUnitName})`;
}

function getResolvedPortionUnitLabel(alternativeDetails, baseUnitDetails) {
  const baseUnitName = getUnitName(baseUnitDetails);
  const conversionRate = firstNumber(
    alternativeDetails?.conversion_rate,
    alternativeDetails?.conversionRate,
  );

  if (!conversionRate || !baseUnitName) return null;

  return `${formatDecimalQuantity(conversionRate)} ${baseUnitName}`;
}

export function getLineUomLabel(line = {}) {
  const alternativeDetails = line.alternativeUnitDetails ?? line.alternative_unit_details;
  const alternativeUnit = line.alternativeUnit ?? line.alternative_unit;
  const alternativeUnitDetails =
    alternativeDetails?.unit_details ?? alternativeDetails?.unitDetails;
  const baseUnitDetails = alternativeDetails?.uom_details ?? alternativeDetails?.uomDetails;
  const unitType = getUnitType(alternativeUnitDetails);

  if (alternativeUnit && alternativeDetails) {
    if (isResolvedPortionUnit(alternativeUnitDetails)) {
      return (
        getResolvedPortionUnitLabel(alternativeDetails, baseUnitDetails) ??
        getUnitName(baseUnitDetails) ??
        resolveHumanUom(line)
      );
    }

    if (unitType === 'WEIGHT' || unitType === 'VOLUME') {
      return (
        getAlternativeUnitQuantityLabel(
          alternativeDetails,
          alternativeUnitDetails,
          baseUnitDetails,
        ) ??
        getUnitName(alternativeUnitDetails) ??
        resolveHumanUom(line)
      );
    }

    return (
      getPackAlternativeUnitLabel(alternativeDetails, alternativeUnitDetails, baseUnitDetails) ??
      getUnitName(baseUnitDetails) ??
      resolveHumanUom(line)
    );
  }

  return resolveHumanUom(line);
}
